import React, { useEffect, useState, useMemo } from 'react'
import { Popover } from 'react-tiny-popover'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import _ from 'lodash'

import PopoverFilterButton from './PopoverFilterButton'
import PopoverWrapper from './PopoverWrapper'
import Range from './Range'

import { filters } from '../../../configuration/filtering'
import { useGetFilteredToursWithException } from '../../../hooks/useGetFilteredToursWithException'
import { useGetMaxValueOfToursField } from '../../../hooks/useGetMaxValueOfToursField'
import { updateFilter } from '../../../redux/actions/applyFilters'
import { getFilterSetByKey } from '../../../redux/reducers/filters'
import { filterToursByField } from '../../../utilities/filters/filterToursByField'

import '../../../styles/scss/filters/popover-slider-filter.scss'

const INITIAL_MIN = 2
const INITIAL_MAX = 10
const INITIAL_RANGE = 15

const PopoverSliderFilter = ({ filterKey }) => {
  const dispatch = useDispatch()
  const activeFilters = useSelector(state => getFilterSetByKey(state, filterKey))
  const [min, setMin] = useState(Number(_.get(activeFilters, 'max'))|| INITIAL_MIN)
  const [max, setMax] = useState(Number(_.get(activeFilters, 'max'))|| INITIAL_MAX)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const tours = useGetFilteredToursWithException(filterKey)
  const totalResults = useMemo(() => {
    return _.size(filterToursByField(tours, filterKey, {min, max}))
  }, [filterKey, min, max, tours])

  const maxToursRange = useGetMaxValueOfToursField(filterKey)
  const range = maxToursRange || INITIAL_RANGE

  useEffect(() => {
    setMin(Number(_.get(activeFilters, 'min'))|| INITIAL_MIN)
    setMax(Number(_.get(activeFilters, 'max'))|| INITIAL_MAX)
  }, [activeFilters, popoverOpen])

  function onClearAllFilters() {
    setPopoverOpen(false)
    dispatch(updateFilter(filterKey, _.get(filters, `[${filterKey}].initialState`)))
  }

  function onOpenPopover() {
    setPopoverOpen(true)
  }

  function onSubmitFilters() {
    setPopoverOpen(false)
    dispatch(updateFilter(filterKey, {min, max}))
  }

  const filterLabel = _.get(filters, `[${filterKey}].label`)
  const hasFilterValues = Boolean(_.get(activeFilters, 'min') || _.get(activeFilters, 'max'))
  const totalResultsLabel = `Show ${totalResults} Result${totalResults === 1 ? '' : 's'}`

  return (
    <div className="popover-slider-filter">
      <Popover
        containerClassName="popover-slider-modal"
        containerParent={document.getElementById('ylg-ms__site-search-listings')}
        containerStyle={{boxShadow: '2px 1px 4px rgba(0, 0, 0, 0.25)'}}
        isOpen={popoverOpen}
        onClickOutside={() => setPopoverOpen(false)}
        positions={['top', 'bottom', 'left', 'right']}
        content={(
          <PopoverWrapper
            onClear={onClearAllFilters}
            onClose={() => setPopoverOpen(false)}
            onSubmit={onSubmitFilters}
            submitLabel={totalResultsLabel}
            title={filterLabel}
          >
            <div className="popover-slider-modal__body">
              <Range
                inputLabel={_.get(filters, `[${filterKey}].placeholder`, '')}
                min={min}
                max={max}
                range={range}
                setMin={(v) => +v >= 0 && +v <= max && setMin(Number(v))}
                setMax={(v) => +v >= 0 && +v <= range && setMax(Number(v))}
                setMinSelect={setMin}
                setMaxSelect={setMax}
              />
            </div>
          </PopoverWrapper>
        )}
      >
        <PopoverFilterButton
          innerId={`${filterKey}-popover-tab`}
          isActive={hasFilterValues}
          label={`${filterLabel}${hasFilterValues ? '(1)' : ''}`}
          onClick={onOpenPopover}
        />
      </Popover>
    </div>
  )
}

PopoverSliderFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
}

export default PopoverSliderFilter
